/**
 * SurveyCard - A standalone survey card component
 * Outline style design with brand logo prominently displayed
 */
import React, { useState } from "react";
import { Survey, SurveysWidgetState, defaultSurveysState } from "./types";

// Campaign type for survey rewards
const CampaignType = {
  SurveyRewardCampaign: "surveyRewardCampaign",
} as const;

// Brandclub primary color for text
const PRIMARY_TEXT_COLOR = "#181d56";

// Survey reward colors (magenta)
const SURVEY_REWARD_COLORS = {
  bg: "rgba(162, 34, 114, 0.3)",
  text: "rgb(162, 34, 114)",
};

// Survey reward icon URL
const SURVEY_REWARD_ICON =
  "https://media.brandclub.com/brandclub/image_asset/survey-reward-icon.webp";

export interface SurveyCardProps {
  /** Survey data to display */
  survey: Survey;
  /** Optional widget state for styling customization */
  state?: Partial<SurveysWidgetState>;
  /** Click handler */
  onClick?: () => void;
  /** Custom class name */
  className?: string;
  /** Card width */
  width?: number | string;
}

const formatMoney = (amount: number | string | null | undefined): string => {
  const numericAmount = Number(amount);
  return `$${Number.isFinite(numericAmount) ? numericAmount.toFixed(2) : "0.00"}`;
};

// Construct brand logo URL from brandId
const getBrandLogoUrl = (brandId: number): string => {
  return `https://media.brandclub.com/b/428x428/${brandId}.jpg`;
};

// Clipboard icon for survey placeholder when no brand logo
const ClipboardIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 40,
  color = "#9ca3af",
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5C15 6.10457 14.1046 7 13 7H11C9.89543 7 9 6.10457 9 5Z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9 12H15"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9 16H13"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * SurveyCard component - Renders a single survey card
 * Outline style with brand logo
 */
export const SurveyCard: React.FC<SurveyCardProps> = ({
  survey,
  state: customState,
  onClick,
  className,
  width = 280,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Merge custom state with defaults (reserved for future theme customization)
  const _state = { ...defaultSurveysState, ...customState };

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      // Default: navigate to survey page
      window.location.href = `/survey/${survey.campaignId}`;
    }
  };

  // Get brand logo URL - prefer brandLogoUrl, then construct from brandId
  const brandLogoUrl =
    survey.brandLogoUrl ||
    (survey.brandId ? getBrandLogoUrl(survey.brandId) : undefined);
  const hasLogo = !!brandLogoUrl;

  return (
    <div
      className={className}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        width: typeof width === "number" ? `${width}px` : width,
        backgroundColor: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: 16,
        overflow: "hidden",
        cursor: "pointer",
        transition: "all 0.2s ease",
        boxShadow: isHovered ? "0 2px 8px rgba(0,0,0,0.06)" : "none",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Top - Brand logo area with white background */}
      <div
        style={{
          height: 160,
          backgroundColor: "#ffffff",
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
        }}
      >
        {hasLogo ? (
          <img
            src={brandLogoUrl}
            alt={survey.brandName}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
          />
        ) : (
          <ClipboardIcon size={48} color="#d1d5db" />
        )}
      </div>

      {/* Bottom - Content area with grey background */}
      <div
        style={{
          padding: 16,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#f9fafb",
        }}
      >
        {/* Title - fixed height for 2 lines */}
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: PRIMARY_TEXT_COLOR,
            lineHeight: 1.4,
            marginBottom: 6,
            height: 42, // Fixed height for 2 lines (15px * 1.4 * 2)
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {survey.title}
        </div>

        {/* Description - fixed height for 1 line */}
        <div
          style={{
            fontSize: 13,
            color: "#6b7280",
            lineHeight: 1.5,
            marginBottom: 12,
            height: 20, // Fixed height for 1 line
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {survey.description ||
            `Share your feedback with ${survey.brandName}.`}
        </div>

        {/* Bottom row - Reward pill with text */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
          }}
        >
          {/* Reward pill */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              backgroundColor: SURVEY_REWARD_COLORS.bg,
              color: SURVEY_REWARD_COLORS.text,
              borderRadius: 25,
              padding: "5px 13px",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            <img
              src={SURVEY_REWARD_ICON}
              alt="survey-reward-icon"
              style={{
                width: 14,
                height: 14,
                objectFit: "contain",
              }}
            />
            <span>{formatMoney(survey.rewardAmount)}</span>
          </div>

          {/* Survey reward text */}
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "#181d56",
            }}
          >
            survey reward
          </span>
        </div>
      </div>
    </div>
  );
};

export default SurveyCard;
